/**
 * CreateDiscussionModal Component
 *
 * Modal for creating new discussions with title, category, and mode selection.
 *
 * Story FOS-4.1.5: Discussion UI & Guided Flow
 * Task 9: Create CreateDiscussionModal
 *
 * Story FOS-4.2.6: Master Components & Pages
 * Task 3: Migrate to governance-suite
 *
 * Features:
 * - Title input (1-200 chars, matching governance format)
 * - Category selector (full canonical set — driven off DISCUSSION_CATEGORIES)
 * - Choose guided vs free-form mode
 * - Create via canister API
 *
 * @module components/governance/CreateDiscussionModal
 * @see AC-4.1.5.1 - Users can create new discussions with guided question flow
 */

import { useState, useCallback, useEffect } from 'react';
import { X, MessageSquare, Lightbulb, FileText, Loader2, AlertCircle } from 'lucide-react';
import { CATEGORY_METADATA, DISCUSSION_CATEGORIES, type DiscussionCategory } from '../../stores/discussionStore';
import { serviceErrorMessage } from '../../utils/serviceErrorMessage';

/**
 * Data for creating a new discussion
 */
export interface CreateDiscussionData {
  title: string;
  category: DiscussionCategory;
  isGuidedMode: boolean;
}

/**
 * Props for CreateDiscussionModal component
 */
export interface CreateDiscussionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when discussion is created */
  onCreate: (data: CreateDiscussionData) => Promise<void>;
  /** Optional class name */
  className?: string;
}

/**
 * Maximum title length (from governance format)
 */
const MAX_TITLE_LENGTH = 200;

/**
 * CreateDiscussionModal - Modal for creating new discussions
 *
 * @example
 * ```tsx
 * <CreateDiscussionModal
 *   isOpen={showCreateModal}
 *   onClose={() => setShowCreateModal(false)}
 *   onCreate={async (data) => {
 *     const id = await createDiscussion(data);
 *     navigateToDiscussion(id);
 *   }}
 * />
 * ```
 */
export function CreateDiscussionModal({
  isOpen,
  onClose,
  onCreate,
  className = '',
}: CreateDiscussionModalProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<DiscussionCategory>('Operational');
  const [isGuidedMode, setIsGuidedMode] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setCategory('Operational');
      setIsGuidedMode(true);
      setError(null);
    }
  }, [isOpen]);

  const titleLength = title.length;
  const isTitleTooLong = titleLength > MAX_TITLE_LENGTH;
  const canSubmit = title.trim().length > 0 && !isTitleTooLong && !isSubmitting;

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setError(null);
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory(e.target.value as DiscussionCategory);
  }, []);

  const handleModeChange = useCallback((guided: boolean) => {
    setIsGuidedMode(guided);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onCreate({
        title: title.trim(),
        category,
        isGuidedMode,
      });
      onClose();
    } catch (err) {
      // bl-1153-1: `err instanceof Error` was UNCONDITIONALLY FALSE here — every
      // error this suite throws is a plain object literal, never an `Error`
      // subclass — so the server's explanation was discarded 100% of the time.
      setError(serviceErrorMessage(err) ?? 'Failed to create discussion');
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, title, category, isGuidedMode, onCreate, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${className}`}
      data-testid="modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-card rounded-lg shadow-lg max-w-lg w-full mx-4 border border-border"
        data-testid="create-discussion-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Start a Discussion</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            data-testid="cancel-button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div className="space-y-1">
            <label
              htmlFor="discussion-title"
              className="block text-sm font-medium text-foreground"
            >
              Title
            </label>
            <input
              id="discussion-title"
              type="text"
              value={title}
              onChange={handleTitleChange}
              placeholder="What would you like to discuss?"
              className={`w-full px-3 py-2 rounded-md border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                isTitleTooLong ? 'border-red-500' : 'border-border'
              }`}
              data-testid="title-input"
            />
            <div className="flex items-center justify-between">
              <div>
                {isTitleTooLong && (
                  <span
                    className="text-xs text-red-600 dark:text-red-400"
                    data-testid="title-error"
                  >
                    Title exceeds maximum length
                  </span>
                )}
              </div>
              <span
                className={`text-xs ${
                  isTitleTooLong ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                }`}
                data-testid="title-char-count"
              >
                {titleLength} / {MAX_TITLE_LENGTH}
              </span>
            </div>
          </div>

          {/* Category */}
          <div className="space-y-1">
            <label
              htmlFor="discussion-category"
              className="block text-sm font-medium text-foreground"
            >
              Category
            </label>
            <select
              id="discussion-category"
              value={category}
              onChange={handleCategoryChange}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground"
              data-testid="category-select"
            >
              {DISCUSSION_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {CATEGORY_METADATA[value].label} - {CATEGORY_METADATA[value].description}
                </option>
              ))}
            </select>
          </div>

          {/* Mode Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Discussion Mode
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleModeChange(true)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  isGuidedMode
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted/50'
                }`}
                data-testid="mode-guided"
                data-selected={isGuidedMode}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb className={`h-4 w-4 ${isGuidedMode ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className={`text-sm font-medium ${isGuidedMode ? 'text-primary' : 'text-foreground'}`}>
                    Guided
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Questions guide you through structuring your idea
                </p>
              </button>

              <button
                type="button"
                onClick={() => handleModeChange(false)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  !isGuidedMode
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted/50'
                }`}
                data-testid="mode-freeform"
                data-selected={!isGuidedMode}
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileText className={`h-4 w-4 ${!isGuidedMode ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className={`text-sm font-medium ${!isGuidedMode ? 'text-primary' : 'text-foreground'}`}>
                    Free-form
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Start with a blank canvas for open discussion
                </p>
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div
              className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-sm"
              data-testid="error-message"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" data-testid="loading-indicator" />
                  Creating...
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4" />
                  Create Discussion
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateDiscussionModal;
